import React from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Sparkles as SparkIcon } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { SERVICE_NODES } from "../../lib/ecosystemData";
import { EcosystemMobile, EcosystemBenefits } from "./ecosystem/EcosystemParts";

/**
 * DIGITAL ECOSYSTEM — Premium hub-and-spoke framework.
 *
 * Reference: orange-monochrome ecosystem diagram (One Partner. Complete Digital Transformation.)
 *  - Centre = glowing JFT hub with concentric rings + dashed ellipse
 *  - 6 service nodes positioned around the hub (top, top-left, top-right,
 *    bottom-left, bottom-right, bottom)
 *  - Animated orange connection curves flow INTO the hub (arrowheads at hub)
 *  - BUSINESS GROWTH outcome card below the diagram
 *  - 5-card benefits bar at the bottom
 *  - Mobile fallback: vertical stack of nodes → hub → outcome → benefits
 */

// Build the curved arrow path from a card position to the hub edge.
// Returns the path string + an array of node points along it.
//
// Hub is 320×320 px inside a max-w-6xl (≈1152px) × 900px section using
// `preserveAspectRatio="none"` SVG, so in the 100×100 viewBox the hub becomes
// an ELLIPSE with rx ≈ 14 (svg-x) and ry ≈ 18 (svg-y). Using a single radius
// (the old code) made horizontal paths visibly stop short of the hub.
const HUB_CX = 50;
const HUB_CY = 44;
const HUB_RX = 14; // svg-% radius along x
const HUB_RY = 18; // svg-% radius along y
const CARD_INSET = 8; // shift the curve START from the card centre toward the hub

const buildCurve = (cx, cy) => {
  const rx = HUB_RX;
  const ry = HUB_RY;
  const bow = 5.5;
  const inset = CARD_INSET;

  // 1) Anchor the start point INSIDE the card edge facing the hub,
  //    so every curve visually departs from the card, not from thin air.
  const dxh = HUB_CX - cx;
  const dyh = HUB_CY - cy;
  const lenh = Math.hypot(dxh, dyh) || 1;
  const sx = cx + (dxh / lenh) * inset;
  const sy = cy + (dyh / lenh) * inset;

  // 2) Endpoint = exact intersection of the card→hub line with the hub ellipse.
  const dx = HUB_CX - sx;
  const dy = HUB_CY - sy;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const t = 1 / Math.sqrt((ux / rx) ** 2 + (uy / ry) ** 2);
  const tx = HUB_CX - ux * t;
  const ty = HUB_CY - uy * t;

  // 3) Control point: perpendicular displacement for a gentle, CONSISTENT
  //    counter-clockwise bow around the hub. All 6 curves now bow the same
  //    rotational direction, giving the diagram a clean orbital flow.
  const mx = (sx + tx) / 2;
  const my = (sy + ty) / 2;
  const perpX = -dy / len; // CCW perpendicular of card→hub
  const perpY = dx / len;
  const ccx = mx + perpX * bow;
  const ccy = my + perpY * bow;

  // 4) Two flow particles sampled along the actual bezier so they ride the curve.
  const bezier = (s) => {
    const u = 1 - s;
    return {
      x: u * u * sx + 2 * u * s * ccx + s * s * tx,
      y: u * u * sy + 2 * u * s * ccy + s * s * ty,
    };
  };
  return {
    d: `M ${sx} ${sy} Q ${ccx} ${ccy} ${tx} ${ty}`,
    nodes: [bezier(0.34), bezier(0.66)],
  };
};

export const DigitalEcosystem = () => {
  const { locale } = useI18n();
  const isDE = locale === "de";

  return (
    <section data-testid="ecosystem" className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 overflow-hidden">
      {/* Ambient background */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[860px] h-[600px] bg-brand-orange/[0.06] blur-[140px]" />
      </div>

      {/* Header */}
      <div className="relative max-w-4xl mx-auto text-center mb-28 md:mb-36">
        <div className="flex items-center justify-center gap-3 mb-5">
          <span className="w-12 h-px bg-brand-orange" />
          <span className="label-mono">{isDE ? "Digitales Ökosystem" : "Digital Ecosystem"}</span>
          <span className="w-12 h-px bg-brand-orange" />
        </div>
        <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-[56px] tracking-tight text-white leading-[1.05]">
          {isDE ? (
            <>Ein Partner. <span className="text-brand-orange">Komplette digitale Transformation.</span></>
          ) : (
            <>One Partner. <span className="text-brand-orange">Complete Digital Transformation.</span></>
          )}
        </h2>
        <p className="mt-5 text-white/65 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
          {isDE
            ? "Alle Fähigkeiten verbunden. Alle arbeiten für dein Business-Wachstum."
            : "All capabilities connected. All working for your business growth."}
        </p>
      </div>

      {/* === DESKTOP / TABLET (md+) : full hub-and-spoke diagram === */}
      <div className="hidden md:block relative max-w-6xl mx-auto" style={{ height: 900 }}>
        {/* Connection SVG layer (behind cards) */}
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full pointer-events-none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="eco-curve" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FF8A2E" stopOpacity="1" />
              <stop offset="100%" stopColor="#FF6A00" stopOpacity="0.85" />
            </linearGradient>
            <filter id="eco-glow-filter" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="0.6" result="b" />
              <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <marker
              id="eco-arrow"
              viewBox="0 0 10 10"
              refX="7"
              refY="5"
              markerWidth="3.6"
              markerHeight="3.6"
              orient="auto-start-reverse"
            >
              <path d="M0 0 L10 5 L0 10 z" fill="#FF6A00" />
            </marker>
          </defs>

          {/* Dashed orbital ellipse around hub */}
          <ellipse
            cx="50" cy="44" rx="32" ry="20"
            fill="none" stroke="rgba(255,106,0,0.45)"
            strokeWidth="0.22" strokeDasharray="0.9 0.9"
            vectorEffect="non-scaling-stroke"
          />

          {/* Curved arrows from each service card → hub edge, with node dots */}
          {SERVICE_NODES.map((n) => {
            // Top-center AI & Automation card sits directly above the hub.
            // It gets a dedicated SINGLE continuous path: a vertical drop from
            // the card's bottom edge (measured: svg-y 11.89 at every md+
            // breakpoint — card is fixed 250px wide / 178px tall inside the
            // fixed 900px container) into a junction node, then a cubic curve
            // whose start tangent is exactly vertical, so straight + curve
            // read as ONE cable. The path starts 0.2 units under the card edge
            // for a seamless butt-joint. An energy pulse travels the full path.
            if (n.slug === "ai-automation") {
              // M start(card edge) L node C c1 c2 end(hub ring, theta≈105°)
              const AI_D = "M 50 11.7 L 50 14.6 C 50 19.5 45.16 26.82 46.38 31.15";
              return (
                <g key={n.slug} filter="url(#eco-glow-filter)">
                  <path
                    d={AI_D}
                    fill="none"
                    stroke="url(#eco-curve)"
                    strokeWidth="0.7"
                    vectorEffect="non-scaling-stroke"
                    markerEnd="url(#eco-arrow)"
                    strokeLinecap="round"
                  >
                    <animate
                      attributeName="stroke-opacity"
                      values="0.65;1;0.65"
                      dur="3.2s"
                      repeatCount="indefinite"
                    />
                  </path>
                  {/* Energy pulse riding the ENTIRE connector, card → hub */}
                  <path
                    d={AI_D}
                    fill="none"
                    stroke="#FFD0A0"
                    strokeWidth="1.1"
                    strokeOpacity="0.95"
                    vectorEffect="non-scaling-stroke"
                    strokeLinecap="round"
                    pathLength="100"
                    strokeDasharray="12 88"
                  >
                    <animate
                      attributeName="stroke-dashoffset"
                      values="0;-100"
                      dur="3.2s"
                      repeatCount="indefinite"
                    />
                  </path>
                  {/* Junction node — the straight segment flows through its centre */}
                  <circle cx="50" cy="14.6" r="0.75" fill="#FF6A00">
                    <animate
                      attributeName="r"
                      values="0.65;0.95;0.65"
                      dur="2.6s"
                      repeatCount="indefinite"
                    />
                  </circle>
                </g>
              );
            }
            const { d, nodes } = buildCurve(n.pos.left, n.pos.top);
            return (
              <g key={n.slug} filter="url(#eco-glow-filter)">
                <path
                  d={d}
                  fill="none"
                  stroke="url(#eco-curve)"
                  strokeWidth="0.7"
                  vectorEffect="non-scaling-stroke"
                  markerEnd="url(#eco-arrow)"
                  strokeLinecap="round"
                >
                  {/* Identical pulse on every path — uniform visual weight */}
                  <animate
                    attributeName="stroke-opacity"
                    values="0.65;1;0.65"
                    dur="3.2s"
                    repeatCount="indefinite"
                  />
                </path>
                {/* Two flow-particles sampled along the actual bezier */}
                {nodes.map((pt, j) => (
                  <circle
                    key={j}
                    cx={pt.x} cy={pt.y} r="0.55"
                    fill="#FF6A00"
                    vectorEffect="non-scaling-stroke"
                  >
                    <animate
                      attributeName="r"
                      values="0.45;0.85;0.45"
                      dur="2.6s"
                      repeatCount="indefinite"
                    />
                  </circle>
                ))}
              </g>
            );
          })}

          {/* Pulsing flow line from hub down to BUSINESS GROWTH outcome */}
          <line
            x1="50" y1="62" x2="50" y2="100"
            stroke="url(#eco-curve)"
            strokeWidth="0.6"
            strokeDasharray="2 2"
            vectorEffect="non-scaling-stroke"
            markerEnd="url(#eco-arrow)"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="0;-20"
              dur="2.2s"
              repeatCount="indefinite"
            />
          </line>
        </svg>

        {/* Centre hub — anchored at 44% top to leave breathing room for the outcome */}
        <div
          className="absolute"
          style={{ left: "50%", top: "44%", transform: "translate(-50%, -50%)" }}
        >
          <div className="relative w-[320px] h-[320px] flex items-center justify-center">
            {/* Outer ambient glow */}
            <div className="absolute inset-[-20px] rounded-full bg-brand-orange/20 blur-[60px]" />
            {/* Pulse rings — slow breathing glow */}
            <div className="absolute inset-0 rounded-full border border-brand-orange/45 eco-hub-breathe" />
            <div className="absolute inset-3 rounded-full border border-brand-orange/35 eco-hub-breathe" style={{ animationDelay: "0.8s" }} />
            <div className="absolute inset-6 rounded-full border border-brand-orange/25 eco-hub-breathe" style={{ animationDelay: "1.6s" }} />
            {/* Inner glow halo */}
            <div className="absolute inset-8 rounded-full bg-brand-orange/20 blur-2xl" />
            {/* Core */}
            <div className="relative w-[240px] h-[240px] rounded-full bg-[#0B0C12] border-2 border-brand-orange/70 shadow-[0_0_90px_rgba(255,106,0,0.55)] flex flex-col items-center justify-center text-center px-6">
              <SparkIcon className="w-7 h-7 text-brand-orange mb-3" />
              <div className="font-display font-bold text-white text-2xl leading-tight">JOSHI</div>
              <div className="font-display font-bold text-white text-2xl leading-tight">
                FUSION<span className="text-brand-orange">TECH</span>
              </div>
              <div className="font-display text-xs tracking-[0.36em] text-white/75 mt-1.5">MEDIA</div>
              <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.22em] text-brand-orange">
                Automate · Innovate · Elevate
              </div>
            </div>
          </div>
        </div>

        {/* Service cards — centre-anchored around hub at 44% */}
        {SERVICE_NODES.map((n) => {
          const Icon = n.icon;
          // map original 50%-anchored coords onto new 44% hub center
          const adjTop = n.pos.top - 6; // shift everything up by 6%
          return (
            <Link
              key={n.slug}
              to={`/services/${n.slug}`}
              data-testid={`ecosystem-node-${n.slug}`}
              className="absolute group w-[250px] z-10"
              style={{
                left: `${n.pos.left}%`,
                top: `${adjTop}%`,
                transform: "translate(-50%, -50%)",
              }}
            >
              <div className="relative glass-strong rounded-2xl p-5 border-brand-orange/15 hover:border-brand-orange/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(255,106,0,0.25)]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-brand-orange/12 border border-brand-orange/45 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-brand-orange" />
                  </div>
                  <div className="font-display font-bold text-white text-[15px] leading-tight group-hover:text-brand-orange transition-colors">
                    {n.label[locale]}
                  </div>
                </div>
                <ul className="space-y-1.5">
                  {n.bullets[locale].map((b, i) => (
                    <li key={i} className="text-[12px] text-white/70 leading-snug flex items-start gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-brand-orange shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Link>
          );
        })}
      </div>

      {/* === MOBILE (< md): vertical stack === */}
      <EcosystemMobile locale={locale} />

      {/* Connector to outcome on mobile */}
      <div className="md:hidden flex justify-center mt-2 mb-4">
        <div className="w-px h-8 bg-brand-orange/50" />
      </div>

      {/* BUSINESS GROWTH — final destination, dramatically emphasised */}
      <div
        className="relative max-w-3xl mx-auto mt-16 md:mt-24 px-4"
        data-testid="ecosystem-outcome"
      >
        {/* Ambient pulsing halo */}
        <div
          aria-hidden
          className="absolute -inset-10 md:-inset-16 rounded-[40px] bg-brand-orange/15 blur-[80px] eco-growth-pulse pointer-events-none"
        />

        <div className="relative glass-strong rounded-[28px] border-2 border-brand-orange/55 px-7 md:px-12 py-9 md:py-12 text-center overflow-hidden shadow-[0_30px_120px_rgba(255,106,0,0.30)] eco-growth-glow">
          {/* radial highlight */}
          <div
            aria-hidden
            className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,106,0,0.22),transparent_65%)] pointer-events-none"
          />
          {/* corner sparks */}
          <div aria-hidden className="absolute top-3 left-3 w-2 h-2 rounded-full bg-brand-orange/70 animate-pulse" />
          <div aria-hidden className="absolute top-3 right-3 w-2 h-2 rounded-full bg-brand-orange/70 animate-pulse" style={{ animationDelay: "0.6s" }} />
          <div aria-hidden className="absolute bottom-3 left-3 w-2 h-2 rounded-full bg-brand-orange/70 animate-pulse" style={{ animationDelay: "1.2s" }} />
          <div aria-hidden className="absolute bottom-3 right-3 w-2 h-2 rounded-full bg-brand-orange/70 animate-pulse" style={{ animationDelay: "1.8s" }} />

          <div className="relative flex flex-col md:flex-row items-center justify-center gap-6">
            <div className="relative w-20 h-20 rounded-2xl bg-brand-orange/20 border border-brand-orange/65 flex items-center justify-center shrink-0 shadow-[0_0_44px_rgba(255,106,0,0.45)]">
              <TrendingUp className="w-9 h-9 text-brand-orange" />
              <span aria-hidden className="absolute inset-0 rounded-2xl border-2 border-brand-orange/60 animate-ping" style={{ animationDuration: "2.4s" }} />
            </div>
            <div className="md:text-left">
              <div className="label-mono text-brand-orange/90 mb-2">
                {isDE ? "Das Ziel" : "The destination"}
              </div>
              <div className="font-display font-extrabold text-3xl md:text-5xl text-white tracking-tight leading-tight">
                {isDE ? "BUSINESS " : "BUSINESS "}
                <span className="text-brand-orange">GROWTH</span>
              </div>
              <div className="mt-3 text-white/80 text-sm md:text-base leading-relaxed max-w-xl">
                {isDE
                  ? "Nachhaltiges Wachstum. Skalierbare Wirkung. Messbare Ergebnisse."
                  : "Sustainable growth. Scalable impact. Measurable results."}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits bar */}
      <EcosystemBenefits locale={locale} isDE={isDE} />
    </section>
  );
};
